import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useBusiness } from './BusinessContext';
import { api } from '../services/api';
import { mergeLiveSessions } from '../utils/liveCallUtils';
import { resolveVoiceBusinessId } from '../utils/voiceBusinessId';

const LiveCallsFeedContext = createContext(null);

const ACTIVE_POLL_MS = 5000;   // a call is live
const IDLE_POLL_MS = 30000;    // nothing live
// Only the newest logs matter for spotting live calls; keep this small (billed per doc).
const LIVE_SCAN_LOG_LIMIT = 8;

function unwrapApiList(res) {
  const payload = res?.data ?? res;
  return Array.isArray(payload) ? payload : [];
}

export function LiveCallsFeedProvider({ children }) {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const businessId = resolveVoiceBusinessId(currentBusiness, user);

  const syncFromBackend = useCallback(
    async (silent = false) => {
      if (!businessId) {
        setSessions([]);
        return [];
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        const [liveRes, logsRes] = await Promise.all([
          api.getLiveInboundCalls(businessId),
          api.getVoiceCallLogs(businessId, LIVE_SCAN_LOG_LIMIT).catch(() => ({ data: [] })),
        ]);
        const liveSessions = unwrapApiList(liveRes);
        const callLogs = unwrapApiList(logsRes);
        const merged = mergeLiveSessions(liveSessions, callLogs, businessId);
        setSessions(merged);
        return merged;
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || 'Failed to load live calls';
        console.warn('[LiveCallsFeed]', msg, { businessId });
        if (!silent) setError(String(msg));
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [businessId],
  );

  const liveCountRef = useRef(0);
  liveCountRef.current = sessions.length;

  // Firestore bills every document read, so poll adaptively:
  //  - hidden tab: no polling (refresh once when the tab becomes visible again)
  //  - live call in progress: fast (ACTIVE_POLL_MS)
  //  - idle: slow (IDLE_POLL_MS)
  useEffect(() => {
    if (!businessId) {
      setSessions([]);
      return undefined;
    }

    let cancelled = false;

    const clearTimer = () => {
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    };

    const schedule = () => {
      clearTimer();
      if (cancelled || document.hidden) return;
      const delay = liveCountRef.current > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      pollRef.current = setTimeout(async () => {
        await syncFromBackend(true);
        schedule();
      }, delay);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
      } else {
        void syncFromBackend(true).then(schedule);
      }
    };

    syncFromBackend(false).then(schedule);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [businessId, syncFromBackend]);

  const value = useMemo(
    () => ({
      businessId,
      sessions,
      liveCount: sessions.length,
      loading,
      error,
      syncFromBackend,
    }),
    [businessId, sessions, loading, error, syncFromBackend],
  );

  return (
    <LiveCallsFeedContext.Provider value={value}>
      {children}
    </LiveCallsFeedContext.Provider>
  );
}

export function useLiveCallsFeed() {
  const ctx = useContext(LiveCallsFeedContext);
  if (!ctx) {
    throw new Error('useLiveCallsFeed must be used within LiveCallsFeedProvider');
  }
  return ctx;
}
